import React from 'react';

import FormInput from './components/FormInput';
import { SS_CONTROL_TYPE } from './constants/form-manager.constants';
import SSAdvancedTextBox from './controls/ssAdvancedTextBox';
import SSButton from './controls/ssButton';
import SSButtonBatch from './controls/ssButtonBatch';
import SSButtonBulkUpload from './controls/ssButtonBulkUpload';
import SSButtonControlledPrintRequest from './controls/ssButtonControlledPrintRequest';
import SSButtonDecisionTree from './controls/ssButtonDecisionTree';
import SSButtonOfficeOnline from './controls/ssButtonOfficeOnline';
import SSButtonScorm from './controls/ssButtonScorm';
import SSButtonSharePoint from './controls/ssButtonSharePoint';
import SSClusterColumnChart from './controls/ssClusterColumnChart';
import SSDataCorrection from './controls/ssDataCorrection';
import SSFieldGroup from './controls/ssFieldGroup';
import SSFileViewer from './controls/ssFileViewer';
import SSForm from './controls/ssForm';
import SSGrid from './controls/ssGrid';
import SSGridColumn from './controls/ssGridColumn';
import SSHyperLink from './controls/ssHyperLink/SSHyperLinkControl';
import SSIcon from './controls/ssIcon';
import SSInspectionMeasurementDataButton from './controls/ssInspectionMeasurementDataButton';
import SSModal from './controls/ssModal';
import SSPanel from './controls/ssPanel';
import SSRepeatingPanel from './controls/ssRepeatingPanel';
import SSRoleActorsManagement from './controls/ssRoleActorsManagement';
import SSSavedSearch from './controls/ssSavedSearch';
import SSSimilarRecords from './controls/ssSimilarRecords';
import SSSingleTab from './controls/ssSingleTab';
import SSStaticText from './controls/ssStaticText';
import SSSubForm from './controls/ssSubForm';
import SSTabSet from './controls/ssTabSet';
import DynamicInput from './DynamicInput';
import { checkTrue, isTrue } from '../utils/commonUtils';

const getComponent = ({ controlType, childrenNodes, ...props }) => {
  const formChangesModeProps = {
    isControlHiddenFCM: props.isControlHiddenFCM,
    isControlAddedFCM: props.isControlAddedFCM,
    isControlChangedFCM: props.isControlChangedFCM,
  };

  switch (controlType) {
    case SS_CONTROL_TYPE.SUB_FORM:
      return <SSSubForm {...props} childrenNodes={childrenNodes} />;
    case SS_CONTROL_TYPE.PANEL:
      return <SSPanel {...props} />;
    case SS_CONTROL_TYPE.RADIO_BUTTON_LIST: {
      return (
        <FormInput
          maxWidth="none"
          isInHeader={props.isInHeader}
          inFieldGroup={props.inFieldGroup}
          id={props.id}
          index={props.index}
          {...formChangesModeProps}
        >
          <DynamicInput {...props} controlType={controlType} />
        </FormInput>
      );
    }
    case SS_CONTROL_TYPE.TEXT_BOX:
    case SS_CONTROL_TYPE.CHECK_BOX:
    case SS_CONTROL_TYPE.DATE_BOX:
    case SS_CONTROL_TYPE.NUMERIC_TEXT_BOX:
    case SS_CONTROL_TYPE.FILE_INPUT:
    case SS_CONTROL_TYPE.SCORM_FILE_INPUT:
    case SS_CONTROL_TYPE.DROP_DOWN:
    case SS_CONTROL_TYPE.SLIDER: {
      return (
        <FormInput
          maxWidth={props.isDetail ? 'none' : 568}
          inFieldGroup={props.inFieldGroup}
          id={props.id}
          index={props.index}
          isInHeader={props.isInHeader}
          parentType={props.parentType}
          controlType={controlType}
          {...formChangesModeProps}
        >
          <DynamicInput {...props} controlType={controlType} />
        </FormInput>
      );
    }
    case SS_CONTROL_TYPE.AI_SUMMARY: {
      return (
        <FormInput
          maxWidth={
            props.isDetail || checkTrue(props.summarizationServiceEnabledFlag) ? 'none' : 568
          }
          inFieldGroup={props.inFieldGroup}
          isInHeader={props.isInHeader}
          id={props.id}
          index={props.index}
          {...formChangesModeProps}
        >
          <DynamicInput {...props} controlType={controlType} />
        </FormInput>
      );
    }
    case SS_CONTROL_TYPE.TEXT_AREA: {
      return (
        <FormInput
          maxWidth={
            props.isDetail || isTrue(props.supportRichText)
              ? 'none'
              : checkTrue(props.translationServiceEnabledFlag)
              ? 608
              : 568
          }
          inFieldGroup={props.inFieldGroup}
          isInHeader={props.isInHeader}
          id={props.id}
          index={props.index}
          {...formChangesModeProps}
        >
          <DynamicInput {...props} controlType={controlType} />
        </FormInput>
      );
    }
    case SS_CONTROL_TYPE.ADVANCED_TEXT_BOX: {
      return (
        <FormInput
          maxWidth={props.isDetail ? 'none' : 568}
          inFieldGroup={props.inFieldGroup}
          isInHeader={props.isInHeader}
          inTable={props.inTable}
          id={props.id}
          index={props.index}
          controlType={controlType}
          {...formChangesModeProps}
        >
          <SSAdvancedTextBox {...props} controlType={controlType} childrenNodes={childrenNodes} />
        </FormInput>
      );
    }
    case SS_CONTROL_TYPE.REPOSITORY_ZOOM:
    case SS_CONTROL_TYPE.ZOOM:
    case SS_CONTROL_TYPE.REGULATORY_TERMS_ZOOM:
      return <DynamicInput {...props} controlType={controlType} />;
    case SS_CONTROL_TYPE.TEXT_VIEW: {
      return props.isInHeader ? (
        <DynamicInput {...props} controlType={controlType} />
      ) : (
        <FormInput
          maxWidth={props.isDetail || props.inTable ? 'none' : 568}
          inTable={props.inTable}
          controlType={controlType}
          inFieldGroup={props.inFieldGroup}
          isInHeader={props.isInHeader}
          parentType={props.parentType}
          id={props.id}
          index={props.index}
          {...formChangesModeProps}
        >
          <DynamicInput {...props} controlType={controlType} />
        </FormInput>
      );
    }
    case SS_CONTROL_TYPE.HYPER_LINK:
      return (
        <FormInput
          maxWidth="none"
          inFieldGroup={props.inFieldGroup}
          id={props.id}
          index={props.index}
          {...formChangesModeProps}
        >
          <SSHyperLink {...props} />
        </FormInput>
      );
    case SS_CONTROL_TYPE.BUTTON:
      return <SSButton {...props} />;
    case SS_CONTROL_TYPE.BUTTON_BATCH:
      return <SSButtonBatch {...props} />;
    case SS_CONTROL_TYPE.BUTTON_BULK_UPLOAD:
      return <SSButtonBulkUpload {...props} />;
    case SS_CONTROL_TYPE.BUTTON_CONTROL_PRINT:
      return <SSButtonControlledPrintRequest {...props} />;
    case SS_CONTROL_TYPE.BUTTON_OFFICE_ONLINE:
      return <SSButtonOfficeOnline {...props} />;
    case SS_CONTROL_TYPE.BUTTON_SHARE_POINT:
      return <SSButtonSharePoint {...props} />;
    case SS_CONTROL_TYPE.BUTTON_DECISION_TREE:
      return <SSButtonDecisionTree {...props} />;
    case SS_CONTROL_TYPE.BUTTON_INSPECTION_MEASUREMENT_DATA:
      return <SSInspectionMeasurementDataButton {...props} />;
    case SS_CONTROL_TYPE.BUTTON_SCORM:
      return <SSButtonScorm {...props} />;
    case SS_CONTROL_TYPE.GRID:
      return <SSGrid {...props} childrenNodes={childrenNodes} />;
    case SS_CONTROL_TYPE.REPEATING_PANEL:
      return <SSRepeatingPanel {...props} childrenNodes={childrenNodes} />;
    case SS_CONTROL_TYPE.GRID_COLUMN:
      return <SSGridColumn {...props} />;
    case 'ssSegmentFieldSet':
      return <div>{props.children}</div>;
    case 'ssSingleSegmentFieldSet':
    case 'ssDoubleSegmentFieldSet':
    case 'ssSegment':
    case 'ssSingleSegment':
    case SS_CONTROL_TYPE.FIELD_SET:
    case SS_CONTROL_TYPE.SUB_GRID:
      return props.children;
    case SS_CONTROL_TYPE.FORM:
      return <SSForm {...props} childrenNodes={childrenNodes} />;
    case SS_CONTROL_TYPE.TAB_SET:
      return <SSTabSet {...props} childrenNodes={childrenNodes} />;
    case SS_CONTROL_TYPE.SINGLE_TAB:
      return <SSSingleTab {...props} />;
    case SS_CONTROL_TYPE.FIELD_GROUP:
      return <SSFieldGroup {...props} />;
    case SS_CONTROL_TYPE.STATIC_TEXT:
      return (
        <FormInput
          maxWidth="none"
          isInHeader={props.isInHeader}
          inFieldGroup={props.inFieldGroup}
          id={props.id}
          index={props.index}
          {...formChangesModeProps}
        >
          <SSStaticText {...props} />
        </FormInput>
      );
    case SS_CONTROL_TYPE.FILE_VIEWER:
      return <SSFileViewer {...props} />;
    case SS_CONTROL_TYPE.SAVED_SEARCH:
      return <SSSavedSearch {...props} />;
    case SS_CONTROL_TYPE.MODAL:
      return <SSModal {...props} />;
    case SS_CONTROL_TYPE.ICON:
      return <SSIcon {...props} />;
    case SS_CONTROL_TYPE.CLUSTER_COLUMN_CHART:
      return <SSClusterColumnChart {...props} />;
    case SS_CONTROL_TYPE.AI_SIMILAR_RECORDS:
      return <SSSimilarRecords {...props} />;
    case SS_CONTROL_TYPE.DATA_CORRECTION:
      return <SSDataCorrection {...props} />;
    case SS_CONTROL_TYPE.ROLE_ACTORS_MANAGEMENT:
      return <SSRoleActorsManagement {...props} />;

    default:
      return (
        <div style={{ marginLeft: 20 }}>
          {`${controlType}${props.label ? ': ' + props.label : ''}`}
          {props.children}
        </div>
      );
  }
};

export default getComponent;
